/**
 * Supported Countries Configuration
 *
 * Centralized list of countries supported for phone authentication.
 * Used by the InternationalPhoneInput component (via react-phone-number-input's
 * `countries` prop) to restrict the country dropdown to only these entries.
 * The library's built-in country select already supports searching by name,
 * ISO code, or dial code — restricting the `countries` list naturally scopes
 * that search to just these countries.
 *
 * Single source of truth: import from here anywhere the country list is needed
 * (login, registration, or any future auth flow) instead of duplicating it.
 */
import type { CountryCode } from 'libphonenumber-js'

export interface SupportedCountry {
  iso: CountryCode
  name: string
  dialCode: string
}

export const SUPPORTED_COUNTRIES: SupportedCountry[] = [
  { iso: 'KW', name: 'Kuwait', dialCode: '+965' },
  { iso: 'US', name: 'United States', dialCode: '+1' },
  { iso: 'JO', name: 'Jordan', dialCode: '+962' },
]

/** ISO codes only, in the shape react-phone-number-input's `countries` prop expects. */
export const SUPPORTED_COUNTRY_ISO_CODES: CountryCode[] = SUPPORTED_COUNTRIES.map((c) => c.iso)

/**
 * Map of ISO code to country data for quick lookup
 */
export const SUPPORTED_COUNTRIES_MAP = new Map(
  SUPPORTED_COUNTRIES.map((country) => [country.iso, country])
)

/**
 * Check if a country is supported
 */
export function isSupportedCountry(iso: string): boolean {
  return SUPPORTED_COUNTRIES_MAP.has(iso as CountryCode)
}

/**
 * Get country data by ISO code
 */
export function getCountryData(iso: string): SupportedCountry | undefined {
  return SUPPORTED_COUNTRIES_MAP.get(iso as CountryCode)
}
